import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useUpdateClaim, type Claim, type ClaimStatus, type ClaimWritePayload } from '@/api/claims';

/*
 * Record a payment or denial against a claim. This is the ONLY path that
 * should ever mutate paid / allowed / PR — PR is not derived from billed
 * minus paid, it's dictated by the payer in the ERA and entered here by the
 * biller reading the EOB (or, eventually, populated from a parsed ERA file).
 *
 * Status is derived once from the entered amounts:
 *   paid >= allowed && paid > 0   → Paid
 *   paid > 0 && paid < allowed    → Partial
 *   denied toggle on               → Denied
 * "Denied" is a distinct user choice because a $0 paid row can also mean a
 * pending/partial review, not a formal denial.
 */

interface RecordPaymentDialogProps {
    claim: Claim;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface FormState {
    allowedAmount: string;
    paidAmount: string;
    prBalance: string;
    paymentDate: string;
    payerRef: string;
    denied: boolean;
    denialReason: string;
}

function defaultForm(c: Claim): FormState {
    return {
        allowedAmount: c.allowedAmount ? String(c.allowedAmount) : '',
        paidAmount: c.paidAmount ? String(c.paidAmount) : '',
        prBalance: c.prBalance ? String(c.prBalance) : '',
        paymentDate: c.paymentDate ? String(c.paymentDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
        payerRef: c.payerRef ?? '',
        denied: c.status === 'Denied',
        denialReason: c.denialReason ?? '',
    };
}

function deriveStatus(form: FormState): ClaimStatus {
    if (form.denied) return 'Denied';
    const paid = Number(form.paidAmount) || 0;
    const allowed = Number(form.allowedAmount) || 0;
    if (paid <= 0) return 'Submitted';
    if (allowed > 0 && paid >= allowed) return 'Paid';
    return 'Partial';
}

export function RecordPaymentDialog({ claim, open, onOpenChange }: RecordPaymentDialogProps) {
    const mutation = useUpdateClaim();
    const [form, setForm] = useState<FormState>(() => defaultForm(claim));

    useEffect(() => {
        if (open) {
            setForm(defaultForm(claim));
            mutation.reset();
        }
    }, [open, claim.id]); // eslint-disable-line react-hooks/exhaustive-deps

    function set<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    const nextStatus = useMemo(() => deriveStatus(form), [form]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        const payload: ClaimWritePayload = {
            allowedAmount: Number(form.allowedAmount) || 0,
            paidAmount: Number(form.paidAmount) || 0,
            prBalance: Number(form.prBalance) || 0,
            paymentDate: form.paymentDate || null,
            payerRef: form.payerRef.trim(),
            status: nextStatus,
            denialReason: form.denied ? form.denialReason.trim() : '',
        };
        try {
            await mutation.mutateAsync({ id: claim.id, payload });
            onOpenChange(false);
        } catch {
            /* surfaced below via mutation.error */
        }
    }

    const serverError = (() => {
        const e = mutation.error;
        if (!e) return null;
        if (e instanceof AxiosError) return e.response?.data?.error ?? 'Failed to record payment';
        return 'Unexpected error';
    })();

    const billed = claim.amount;
    const adjustment = Math.max(0, billed - (Number(form.allowedAmount) || 0));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[min(calc(100vw-2rem),36rem)]">
                <DialogHeader>
                    <DialogTitle>Record Payment — {claim.claimNo}</DialogTitle>
                    <DialogDescription>
                        Enter values from the ERA / EOB. Patient responsibility is set by the payer, not calculated by the app.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {serverError && <Alert variant="danger" className="mb-4">{serverError}</Alert>}

                    <div className="mb-4 p-3 bg-surface-muted rounded-md text-xs grid grid-cols-2 gap-x-4 gap-y-1">
                        <div className="text-text-subtle uppercase tracking-wider text-2xs">Billed</div>
                        <div className="text-right tabular-nums">{formatMoney(billed)}</div>
                        <div className="text-text-subtle uppercase tracking-wider text-2xs">Contractual Adj. (derived)</div>
                        <div className="text-right tabular-nums text-text-muted">{formatMoney(adjustment)}</div>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4">
                        <FormField label="Allowed Amount" required hint="From the ERA adjudication.">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={form.allowedAmount}
                                    onChange={(e) => set('allowedAmount', e.target.value)}
                                    disabled={form.denied}
                                />
                            )}
                        </FormField>

                        <FormField label="Insurance Paid" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={form.paidAmount}
                                    onChange={(e) => set('paidAmount', e.target.value)}
                                    disabled={form.denied}
                                />
                            )}
                        </FormField>

                        <FormField label="Patient Responsibility (PR)" hint="From the ERA — not billed − paid.">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={form.prBalance}
                                    onChange={(e) => set('prBalance', e.target.value)}
                                    disabled={form.denied}
                                />
                            )}
                        </FormField>

                        <FormField label="Payment Date">
                            {(id) => (
                                <Input
                                    id={id}
                                    type="date"
                                    min="1900-01-01"
                                    max={new Date().toISOString().slice(0, 10)}
                                    value={form.paymentDate}
                                    onChange={(e) => set('paymentDate', e.target.value)}
                                    disabled={form.denied}
                                />
                            )}
                        </FormField>

                        <FormField
                            label="Payer Reference #"
                            hint="EOB / check / EFT trace #"
                            className="col-span-2"
                        >
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.payerRef}
                                    onChange={(e) => set('payerRef', e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <label className="flex items-center gap-2 my-3 text-sm text-text">
                        <input
                            type="checkbox"
                            checked={form.denied}
                            onChange={(e) => set('denied', e.target.checked)}
                            className="w-4 h-4 accent-accent"
                        />
                        Mark as denied
                    </label>

                    {form.denied && (
                        <FormField label="Denial Reason" required>
                            {(id) => (
                                <Textarea
                                    id={id}
                                    rows={2}
                                    placeholder="e.g., CO-45: Charge exceeds fee schedule"
                                    value={form.denialReason}
                                    onChange={(e) => set('denialReason', e.target.value)}
                                />
                            )}
                        </FormField>
                    )}

                    <div className="mt-3 p-3 bg-surface-muted rounded-md text-xs text-text-muted">
                        New status: <span className="font-medium text-text">{nextStatus}</span>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={mutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={mutation.isPending}>
                            {mutation.isPending ? 'Saving…' : form.denied ? 'Save denial' : 'Record payment'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function formatMoney(n: number): string {
    return (n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}
